'use client'

import {DatePickerProps} from 'antd'

import React, {useEffect, useMemo, useRef, useState} from 'react'
import {useController} from 'react-hook-form'

import {useFormControl} from '@/src/shared/modules/form'
import 'react-datepicker/dist/react-datepicker.css'
import styled from 'styled-components'
import {Label} from '@/src/shared/ui/label'

import * as UI from './date-picker.styled'
import {ChevronDown} from 'lucide-react'

export interface Props extends React.PropsWithChildren, IInputProps, Omit<DatePickerProps, 'name'> {
  className?: string
}

const DatePicker = ({children, ...props}: Props) => {
  const formControl = useFormControl()
  const {field, fieldState} = useController({name: props.name, control: formControl.control})

  const ref = useRef<Nullable<HTMLDivElement>>(null)

  const componentProps = {
    ...props,
    getPopupContainer: () => ref.current!,
    defaultValue:
      field.value && typeof field.value === 'string'
        ? field.value.split('.').reduce(
            (acc, num, index) => {
              switch (index) {
                case 0:
                  acc.day = parseInt(num)
                  break
                case 1:
                  acc.month = parseInt(num)
                  break
                case 2:
                  acc.year = parseInt(num)
                  break
              }
              return acc
            },
            {year: new Date().getFullYear(), month: new Date().getMonth() + 1, day: new Date().getDate()},
          )
        : (field.value ?? undefined),
    onChange: (date: string) => field.onChange(date),
    status: fieldState.invalid ? 'error' : undefined,
  }

  if (children) {
    return (
      <Wrapper className={props.className} ref={ref}>
        <Label>{children}</Label>
        <DateSelect
          onChange={dateParts => {
            componentProps.onChange(`${dateParts.day}.${dateParts.month}.${dateParts.year}`)
          }}
          value={componentProps.value ?? componentProps.defaultValue}
        />
      </Wrapper>
    )
  }
  return (
    <DateSelect
      onChange={dateParts => {
        componentProps.onChange(`${dateParts.day}.${dateParts.month}.${dateParts.year}`)
      }}
      value={componentProps.value ?? componentProps.defaultValue}
    />
  )
}

// Types
export type DateValue = {
  year: number
  month: number // 1-12
  day: number // 1-31
}

type DateSelectProps = {
  minYear?: number
  maxYear?: number
  value?: DateValue
  onChange?: (value: DateValue) => void
}

// Utility: days in month
const getDaysInMonth = (year?: number, month?: number): number => {
  if (!year || !month) return 31
  return new Date(year, month, 0).getDate()
}

const generateRange = (start: number, end: number): number[] => {
  const arr: number[] = []
  for (let i = start; i <= end; i++) arr.push(i)
  return arr
}

function DateSelect({minYear = 1900, maxYear = new Date().getFullYear(), value, onChange}: DateSelectProps) {
  const dayRef = useRef<HTMLElement | undefined>(undefined)
  const monthRef = useRef<HTMLElement | undefined>(undefined)
  const yearRef = useRef<HTMLElement | undefined>(undefined)

  const today = new Date()

  const [year, setYear] = useState<number>(value?.year ?? today.getFullYear())
  const [month, setMonth] = useState<number>(value?.month ?? today.getMonth() + 1)
  const [day, setDay] = useState<number>(value?.day ?? today.getDate())

  const years = useMemo<number[]>(() => generateRange(minYear, maxYear), [minYear, maxYear])

  const months = useMemo<number[]>(() => generateRange(1, 12), [])

  const monthTitles = useMemo<Record<number, string>>(
    () => ({
      1: 'Янв',
      2: 'Фев',
      3: 'Мар',
      4: 'Апр',
      5: 'Мая',
      6: 'Июн',
      7: 'Июл',
      8: 'Авг',
      9: 'Сен',
      10: 'Окт',
      11: 'Ноя',
      12: 'Дек',
    }),
    [],
  )

  const maxDays = useMemo<number>(() => getDaysInMonth(year, month), [year, month])

  const days = useMemo<number[]>(() => {
    return generateRange(1, maxDays)
  }, [maxDays, day])

  // Emit change
  useEffect(() => {
    if (onChange) {
      onChange({year, month, day})
    }
  }, [year, month, day, onChange])

  // Sync external value
  useEffect(() => {
    if (!value) return

    if (value.year !== year || value.month !== month || value.day !== day) {
      setYear(value.year)
      setMonth(value.month)
      setDay(value.day)
    }
  }, [value])

  return (
    <UI.Component>
      {/* Day */}
      <UI.Select
        value={day}
        ref={r => {
          dayRef.current = r?.nativeElement
        }}
        suffixIcon={<ChevronDown size={18} />}
        options={days.map(num => ({
          key: num,
          label: num,
          value: num,
        }))}
        onChange={value => {
          setDay(Number(value))
        }}
        className="border rounded px-2 py-1"
      />

      {/* Month */}
      <UI.Select
        value={month}
        ref={r => {
          monthRef.current = r?.nativeElement
        }}
        suffixIcon={<ChevronDown size={18} />}
        onChange={value => {
          const newMaxDays = getDaysInMonth(year, Number(value))
          if (day > newMaxDays) {
            setDay(newMaxDays)
          }
          setMonth(Number(value))
        }}
        className="border rounded px-2 py-1"
        options={months.map(num => ({
          key: num,
          label: monthTitles[num],
          value: num,
        }))}
      />

      {/* Year */}
      <UI.Select
        value={year}
        ref={r => {
          yearRef.current = r?.nativeElement
        }}
        suffixIcon={<ChevronDown size={18} />}
        onChange={value => setYear(Number(value))}
        className="border rounded px-2 py-1"
        options={years.map(num => ({
          key: num,
          label: num,
          value: num,
        }))}
      />
    </UI.Component>
  )
}

export const Wrapper = styled.div`
  position: relative;
`

export default DatePicker
